import { useRouter } from 'next/router';
import React, { useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import striptags from 'striptags';
import URI from 'urijs';
import { ICONS } from 'Components/utils/icon/icons';
import { IS_VALID, NOT_VALIDATED } from 'Components/frameworks/form-validator-hoc/validationStates';
import { useTrackSearchResults } from 'Components/widgets/facet-search/useTrackSearchResults';
import { usePageContext } from 'Context/PageContext';
import { useAppContext } from 'Context/ReactAppContext';
import { t } from 'Src/i18n';
import { useAutocomplete } from 'Src/hooks/use-autocomplete/useAutocomplete';
import { changeSearchQuery } from 'Store/faceting/facetingSearch.action';
import { setKeywords } from 'Store/search/query/searchQuery.action';
import { keywordsSelector } from 'Store/search/search.selector';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import { InputDatalist } from 'Components/frameworks/form-inputs/input-datalist/InputDatalist';
import {
  ACTIONS,
  useCriteriaDisplayContext,
} from 'Components/widgets/criteria/CriteriaDisplayContext';
import { CriteriaContext } from 'Components/widgets/criteria/CriteriaContext';

const ItemLayout = ({ label }) => {
  if (!label) {
    return null;
  }

  return (
    <div className="flex cursor-pointer items-center overflow-hidden p-10 py-15 transition-colors duration-250 ease-in hocus:bg-black hocus:text-white">
      <span className="decorator-match truncate font-sans text-12">{label}</span>
    </div>
  );
};

const ItemTitle = ({ title }) => (
  <div className="py-10 font-sans text-10 font-bold text-sienna">{title}</div>
);

type Props = {
  onFocus?: () => void;
  onQueryChange: (query: SearchQuery) => unknown;
  suggestedRequests?: CMSFacetingSuggestedRequests;
};
const edito = 'edito';
export const CriteriaQuery = (props: Props) => {
  const { onFocus, onQueryChange, suggestedRequests } = props;
  const { pageId } = usePageContext();
  const { isSeller } = useAppContext();
  const [criteriaContext] = useContext(CriteriaContext);
  const [, dispatchCriteriaDisplayContext] = useCriteriaDisplayContext();
  const keywords: string = useSelector(keywordsSelector);
  const query: SearchQuery = useSelector((state) => state.faceting.query);
  const dispatch = useDispatch();
  const router = useRouter();
  const {
    query: { locale },
  } = router;
  const track = useTracking({
    actionGA: 'search',
  });
  const { setAutocompleteQuery, autocompleteResults, setAutocompleteResults } =
    useAutocomplete(keywords);
  const value = query && query.label ? query.label : keywords;
  const trackSearchResults = useTrackSearchResults();
  const hasSuggestedRequests = !!suggestedRequests?.items?.length;
  return (
    <InputDatalist
      dataList={
        value || isSeller
          ? autocompleteResults
          : (suggestedRequests?.items || []).map(({ label, url: link }) => ({
              id: label,
              label,
              link,
            }))
      }
      hasClear
      hasValidationIcon={false}
      iconLeft={ICONS.SEARCH_GLASS}
      isAnimated
      itemLayout={!value ? ItemLayout : undefined}
      itemTitle={
        !value && hasSuggestedRequests ? <ItemTitle title={suggestedRequests?.title} /> : undefined
      }
      onChange={(_, v: string) => {
        const cleanKeywords = striptags(v);
        dispatch(setKeywords(cleanKeywords));
        setAutocompleteQuery(cleanKeywords);
        onQueryChange({
          label: cleanKeywords,
        });
      }}
      onClear={() => {
        setAutocompleteResults([]);
        const emptyQuery = {
          label: '',
        };

        if (!keywords) {
          // User is clearing the search query
          dispatch(changeSearchQuery(emptyQuery));
        }

        onQueryChange(emptyQuery);
        dispatch(setKeywords(''));
        setAutocompleteQuery('');
      }}
      onFocus={() => {
        if (onFocus) {
          onFocus();
        }

        if (!isSeller && !value && hasSuggestedRequests) {
          track({
            labelGA: 'requestsDisplay',
          });
        }
      }}
      onSelect={({ label, item: { type, productType, link } }) => {
        const isEdito = type === edito;

        if (value && !isEdito) {
          const searchQuery = {
            label,
            link,
            productType,
            type,
          };
          dispatch(changeSearchQuery(searchQuery));
          dispatchCriteriaDisplayContext({
            type: ACTIONS.OPEN_NEXT_CRITERIA,
            payload: {
              currentCriteria: 'criteriaQuery',
              criteriaContext,
            },
          });
          onQueryChange(searchQuery);
        } else if (link) {
          const redirection = isEdito ? 'redirection_editorial' : 'redirection_suggestedRequest';
          const isSearchUrl = link.startsWith('/s');
          track({
            internalSearchEngineNbResults: isSearchUrl ? '' : redirection,
            labelGA: isEdito ? 'editorial' : 'suggestedRequest',
            searchReferrer: pageId,
            searchRequestType: 'Autocomplete',
            searchTerms: label,
          });
          const url = locale
            ? new URI(link)
                .addQuery({
                  locale,
                })
                .toString()
            : link;
          router.push(url);

          if (!isSearchUrl) {
            trackSearchResults({
              internalSearchEngineNbResults: redirection,
              searchRequestType: 'Autocomplete',
              searchResorts: [],
              searchTerms: label,
            });
          }
        }
      }}
      placeholder={t('faceting.searchTitle')}
      value={value}
      validationState={value ? IS_VALID : NOT_VALIDATED}
    />
  );
};
